import React from 'react';
import PropTypes from 'prop-types';

import { crudConfig, uiConfig } from '../../conf/criteria';

// // API Hooks
import { useCriterias } from '../api/hooks/useCriterias';
import { useDeleteCriteria } from '../api/hooks/useDeleteCriteria';
import { useDuplicateCriteria } from '../api/hooks/useDuplicateCriteria';
import { useExportCriteria } from '../api/hooks/useExportCriteria';
import { useImportCriteria } from '../api/hooks/useImportCriteria';

// UI Components
import { Button } from '../../../components/Button';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { FileImportDialog } from '../../../components/FileImportDialog';
import Table from '../../../components/Table';
import { AddEditWrapper } from './components/addEdit';

import { translateUIConfigForTable, translateUIConfigForAddEdit } from '../../../common/utility';

const { READ_ONLY_FEATURES } = process.env;

function getRandomSeconds(maxSeconds) {
    return Math.floor(Math.random() * maxSeconds);
}

const CriteriaManager = ({ userContext }) => {
    const { sessionToken } = userContext;
    const [editing, setEditing] = React.useState(null);
    const [importing, setImporting] = React.useState(false);
    const [hasImported, setHasImported] = React.useState(false);
    const [deleting, setDeleting] = React.useState(null);
    const [exporting, setExporting] = React.useState(null);
    const [copying, setCopying] = React.useState(null);
    const [settledUploads, setSettledUploads] = React.useState([]);
    const saveDisabled = READ_ONLY_FEATURES === 'true';
    const { data, isLoading } = useCriterias(sessionToken);
    const deleteCriteria = useDeleteCriteria({
        sessionToken,
        onSuccessCb: () => {
            setDeleting(null);
        }
    });
    const downloadCriteriaExport = useExportCriteria({
        sessionToken
    });
    const duplicateCriteria = useDuplicateCriteria({
        sessionToken,
        onSuccessCb: () => {
            setCopying(null);
        }
    });
    const importCriteria = useImportCriteria({
        sessionToken,
        onSuccessCb: () => {
            setHasImported(true);
            setCopying(null);
        },
        onErrorCb: () => {
            setHasImported(false);
        }
    });
    const { columns, actions, initialState } = translateUIConfigForTable(
        uiConfig,
        setEditing,
        setDeleting,
        null,
        setExporting,
        setCopying
    );
    const tableData = React.useMemo(() => {
        if (!data) return [];
        if (actions != null) {
            return actions != null
                ? data.map((v) => ({
                      ...v,
                      DataTableActions: actions.map((i) => ({
                          ...i,
                          action: () => i.action(v[uiConfig.primaryKey])
                      }))
                  }))
                : data;
        } else {
            return data;
        }
    }, [data, actions]);

    const getItemById = React.useCallback(
        (id) => {
            return tableData.find((item) => item[uiConfig.primaryKey] === id);
        },
        [tableData]
    );

    const deletingDTO = React.useMemo(() => {
        return deleting ? getItemById(deleting) : null;
    }, [deleting, getItemById]);

    const exportingDTO = React.useMemo(() => {
        return exporting ? getItemById(exporting) : null;
    }, [exporting, getItemById]);

    const copyingDTO = React.useMemo(() => {
        return copying ? getItemById(copying) : null;
    }, [copying, getItemById]);

    const closeAddEdit = () => {
        setEditing(null);
    };

    const closeDelete = () => {
        setDeleting(null);
    };
    const isExistingDoc = (doc) => {
        return (
            (data ?? []).find((v) => {
                return v.criteriaId !== doc.criteriaId && v.name === doc.name;
            }) !== undefined
        );
    };

    const controlButtons = [];
    if (crudConfig.functions.insert) {
        controlButtons.push(
            <Button key={'add-new'} appearance="primary" onClick={() => setEditing('new')}>
                Add New Criteria
            </Button>
        );
    }
    if (crudConfig.functions.import) {
        controlButtons.push(
            <Button key={'import'} appearance="primary" onClick={() => setImporting(true)}>
                Import
            </Button>
        );
    }

    return (
        <>
            <Table
                withHeader={true}
                columns={columns}
                data={tableData}
                withActions={actions !== null}
                id={'criteria-manager'}
                label={'Criteria Manager'}
                showControlDeck={true}
                paginated={true}
                loading={isLoading}
                controlButtons={controlButtons}
                initialState={initialState}
            />
            {editing != null && (
                <AddEditWrapper
                    formModel={translateUIConfigForAddEdit(uiConfig)}
                    saveDisabled={saveDisabled}
                    selectedCriteriaId={editing}
                    closeDialog={closeAddEdit}
                    sessionToken={sessionToken}
                    isExistingDoc={isExistingDoc}
                    setEditingPredicate={setEditing}
                    allCriteria={data}
                    onMount={typeof uiConfig.onFormMount === 'function' ? uiConfig.onFormMount : undefined}
                />
            )}
            {deletingDTO != null ? (
                <ConfirmationModal
                    header={`Delete Criteria?`}
                    body={`Are you sure you want to delete ${deletingDTO.name}?`}
                    confirmButtonText={'Yes, Delete'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={closeDelete}
                    onConfirm={() => {
                        return new Promise((resolve) => {
                            deleteCriteria.mutate(deletingDTO?.criteriaId);
                            resolve();
                        });
                    }}
                />
            ) : null}
            {exportingDTO != null ? (
                <ConfirmationModal
                    header={`Export Criteria?`}
                    body={`Are you sure you want to download an export of ${exportingDTO.name}?`}
                    confirmButtonText={'Yes, Download'}
                    // confirmButtonProps={{ text: true }}
                    closeDialog={() => {
                        setExporting(null);
                    }}
                    onConfirm={() => {
                        return new Promise((resolve) => {
                            downloadCriteriaExport.mutate(exportingDTO?.criteriaId);
                            resolve();
                        });
                    }}
                />
            ) : null}
            {copyingDTO != null ? (
                <ConfirmationModal
                    header={`Duplicate Criteria?`}
                    body={`Would you like to copy ${copyingDTO.name}?`}
                    confirmButtonText={'Yes, Copy'}
                    closeDialog={() => {
                        setCopying(null);
                    }}
                    onConfirm={() => {
                        return new Promise((resolve) => {
                            duplicateCriteria.mutate(copyingDTO);
                            resolve();
                        });
                    }}
                />
            ) : null}
            {importing === true ? (
                <FileImportDialog
                    acceptedMIMETypesMap={{ 'application/json': ['.json'] }}
                    closeDialog={() => {
                        setImporting(false);
                        setHasImported(false);
                        setSettledUploads([]);
                    }}
                    onImport={async (files = []) => {
                        const fileReadPromises = files.map(
                            (file) =>
                                new Promise((resolve) => {
                                    const reader = new FileReader();

                                    reader.onload = async (event) => {
                                        const jsonData = JSON.parse(event.target.result);

                                        resolve(jsonData);
                                    };

                                    reader.readAsText(file);
                                })
                        );
                        const jsonDataArray = await Promise.all(fileReadPromises);
                        const settled = [];

                        const promises = jsonDataArray.map((jsonData) => {
                            return importCriteria.mutateAsync(jsonData).then(() => {
                                setTimeout(() => {
                                    settled.push(jsonData.criteriaId);
                                    setSettledUploads([...settledUploads, ...settled]);
                                }, getRandomSeconds(2));
                            });
                        });

                        try {
                            return Promise.all(promises);
                        } catch (e) {
                            return Promise.reject(e);
                        }
                    }}
                    isImporting={importCriteria.isLoading}
                    hasImported={hasImported}
                    title={'Import Criteria'}
                    description={
                        'Click or drag JSON files on to the dropzone below to stage them for import, then click the Import Files button.'
                    }
                    settledUploads={settledUploads}
                />
            ) : null}
        </>
    );
};

export default CriteriaManager;

CriteriaManager.propTypes = {
    userContext: PropTypes.object
};
